<template>
    <Head>
        <Title>{{ course.title }}</Title>
        <Meta name="description" :content="descriptionSanitised" />
        <Meta property="og:title" :content="course.title" />
        <Meta property="og:description" :content="descriptionSanitised" />
        <Meta property="og:image" :content="isThumbnailOutdated ? course.image : course.thumbnail" />
        <Meta name="twitter:title" :content="course.title" />
        <Meta name="twitter:description" :content="descriptionSanitised" />
        <Meta name="twitter:image" :content="isThumbnailOutdated ? course.image : course.thumbnail" />
    </Head>
    <div v-if="isAuthorised && _course">
        <ClientOnly>
            <Player
                v-if="_course"
                :course="_course"
            />
        </ClientOnly>
    </div>
    <main
        v-else-if="course || isUnavailable || inactive || requirePayment || requireIdentification"
        :class="{ inactive }"
    >
        <ClientOnly v-if="inactive || isUnavailable">
            <Inactive :course="_course || course" />
        </ClientOnly>
        <ClientOnly v-else-if="requirePayment">
            <Paywall
                :course="_course || course"
                @purchased="
                    setToken($event);
                    onPurchase();
                "
                @authorised="
                    setToken($event);
                    getCourse();
                "
            />
        </ClientOnly>
        <ClientOnly v-else-if="requireIdentification">
            <Access
                :course="_course || course"
                @authorised="
                    setToken($event);
                    getCourse();
                "
            />
        </ClientOnly>
    </main>
</template>

<script>
definePageMeta({ layout: 'public' });

import { presets } from '@/helpers/theme';
import { url, isEmptyObject, sanitise } from '@/helpers/utilities';
import { defineAsyncComponent } from 'vue';
import useTracking from '@/store/tracking';

const Player = defineAsyncComponent(() => import('@/components/author/course/player'));
const Access = defineAsyncComponent(() => import('@/components/author/course/access'));
const Paywall = defineAsyncComponent(() => import('@/components/author/course/paywall'));
const Inactive = defineAsyncComponent(() => import('@/components/author/course/inactive'));

export default defineNuxtComponent({
    name: 'CourseLearn',
    components: { Player, Access, Paywall, Inactive },
    data() {
        return {
            _course: null,
            purchased: false,
            viewed: false,

            isPreview: false,
            isKeyValid: null,
            isUnavailable: false
        };
    },
    async asyncData(context) {
        const course = await $fetch(context.$config.public.baseURL + `/learn/${context._route.params.key}`);
        course.theme = !course.theme || isEmptyObject(course.theme) ? presets[0] : course.theme;
        return { course };
    },
    computed: {
        key() {
            return this.$route.params.key;
        },
        keyType() {
            return this.key.length === 32 ? 'preauth' : 'key';
        },
        inactive() {
            return (this._course && !this._course.active)
                || !this.key;
        },
        requireIdentification() {
            return this._course
                && this._course.identified
                && !this._course.identifier;
        },
        requirePayment() {
            return this._course
                && this._course.paid
                && this._course.price > 0
                && !this._course.purchased;
        },
        isAuthorised() {
            return !this.inactive
                && !this.requireIdentification
                && !this.requirePayment;
        },
        descriptionSanitised() {
            return sanitise(this.course.description);
        },
        isThumbnailOutdated() {
            if (!this.course.thumbnail) return true;
            const thumbnailPath = this.course.thumbnail?.split('/').pop()?.split('.')[0];
            return thumbnailPath !== this.course.thumbnailRevision;
        }
    },
    watch: {
        isAuthorised() {
            this.onAuthorised();
        }
    },
    mounted() {
        const session = localStorage.getItem('coassemble:sessionToken');
        if (session) {
            try {
                const { token, expiry } = JSON.parse(session);
                if (expiry && this.$date(expiry).isAfter(this.$date())) {
                    this.setToken(token);
                } else {
                    localStorage.removeItem('coassemble:sessionToken');
                }
            } catch (e) {
                console.error(e);
                localStorage.removeItem('coassemble:sessionToken');
            }
        }

        const params = this.$route.query;
        if (params.preview) this.isPreview = true;
        if (params.token) this.setToken(params.token);
        if (this.keyType === 'preauth') {
            return this.checkKey();
        } else {
            if (!this.course) return this.isUnavailable = true;
            this.getCourse();
        }
    },
    methods: {
        url,
        checkKey() {
            this.isAuthorising = true;
            return this.$http
                .get(`/learn/verify/${this.key}`)
                .then(({ data }) => {
                    if (data && data.token) this.setToken(data.token);
                })
                .finally(() => this.getCourse());
        },
        setToken(token) {
            this.$http.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            useTracking.setToken(token);
        },
        getCourse(overrides = {}) {
            if (!this.key) return;
            return this.$http.get(`/learn/${this.key}/course`)
                .then(({ data }) => {
                    data.theme = !data.theme || isEmptyObject(data.theme) ? presets[0] : data.theme;
                    data.theme.colours = !data.theme.colours ? presets[0].colours : data.theme.colours;
                    this._course = { ...data, ...overrides };
                    this.setToken(data.token);
                    localStorage.setItem('coassemble:sessionToken', JSON.stringify({ token: data.token, expiry: this.$date().add(30, 'minutes').toISOString() }));

                    this.onAuthorised();
                })
                .catch(() => this.isUnavailable = true);
        },
        onPurchase() {
            this.purchased = true;
            this.getCourse({ purchased: true });
        },
        onAuthorised() {
            if (this.isPreview) return;
            if (this.isAuthorised && !this.viewed && this._course) {
                const embedded = window.top !== window.self;
                this.$http.get(`/learn/${this._course.id}/viewed`, { params: { embedded, purchased: this.purchased } });
                this.viewed = true;
            }
        }
    }
});
</script>

<style lang="less">
html, body {
    height: 100dvh;
    overflow: hidden;
    overscroll-behavior: auto;
    -webkit-overflow-scrolling: touch;
}
</style>

<style lang="less" scoped>
main {
    height: 100dvh;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    display: flex;

    .course-access { display: contents; }
    &.inactive {
        background-color: var(--cloud);
        align-items: center;
        justify-content: center;
    }
}
</style>
